import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminOrderService, GetOrdersParams } from '@/services/admin-order.service'
import { toast } from 'sonner'
import { AxiosError } from 'axios'
import { awaitRefresh } from '@/lib/awaitRefresh'

/**
 * Hook to fetch orders with pagination and filters
 */
export function useAdminOrders(params: GetOrdersParams) {
  return useQuery({
    queryKey: ['admin-orders', params],
    queryFn: () => adminOrderService.getOrders(params),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data
  })
}

/**
 * Hook to fetch order details
 */
export function useAdminOrderDetail(orderId: string) {
  return useQuery({
    queryKey: ['admin-order', orderId],
    queryFn: () => adminOrderService.getOrderDetails(orderId),
    enabled: !!orderId,
    staleTime: 30000,
  })
}

/**
 * Resolves a batch of order ids to `{ id, orderNumber, type }`, keyed by id.
 *
 * For a screen that lists rows carrying an order id but not the order itself — the billing-cycle
 * list is the first. It is one request per page rather than one per row, and the ids are sorted
 * into the query key so a re-render that reorders the same page does not look like a new query.
 *
 * Returns a Map so the caller looks a row up in O(1) rather than scanning the array per row.
 */
export function useOrdersByIds(orderIds: string[]) {
  // Sorted and de-duplicated: the key has to describe the SET of ids, not the order they happened
  // to arrive in, or every re-sort of the table refetches the same orders.
  const ids = Array.from(new Set(orderIds)).sort()

  return useQuery({
    queryKey: ['admin-orders-by-ids', ids],
    queryFn: async () => {
      const summaries = await adminOrderService.getOrdersByIds(ids)
      return new Map(summaries.map((summary) => [summary.id, summary]))
    },
    enabled: ids.length > 0,
    // Longer than a list query: an order's number and type never change once it exists, so this
    // is effectively immutable data being used as a label.
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch order statistics
 */
export function useOrderStatistics(params: {
  fromDate?: string
  toDate?: string
  tenantId?: string
}) {
  return useQuery({
    queryKey: ['admin-order-statistics', params],
    queryFn: () => adminOrderService.getStatistics(params),
    staleTime: 60000, // 1 minute
  })
}

/**
 * Hook to complete an order manually
 */
export function useCompleteOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      orderId,
      paymentId,
      invoiceId,
    }: {
      orderId: string
      paymentId: string
      invoiceId: string
    }) => adminOrderService.manuallyCompleteOrder(orderId, { paymentId, invoiceId }),
    onSuccess: () => {
      toast.success('Order completed successfully')
    },
    onError: (error: AxiosError | Error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete order'
      toast.error(errorMessage)
    },
    // Returned so the mutation stays `isPending` until the refetches it triggers have landed —
    // the caller (the confirm dialog / BusyOverlay) closes only once fresh data is on screen,
    // not on the bare API answer. Runs on success and failure alike. Capped by `awaitRefresh`:
    // past ~8s the overlay lifts anyway rather than staying up for however long a stalled re-read
    // takes — the write itself already landed.
    onSettled: (_data, _error, variables) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ['admin-orders'] }),
          queryClient.invalidateQueries({ queryKey: ['admin-order', variables.orderId] }),
          queryClient.invalidateQueries({ queryKey: ['admin-order-statistics'] }),
        ]),
      ),
  })
}

/**
 * Hook to cancel an order
 */
export function useCancelOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: string; reason: string }) =>
      adminOrderService.cancelOrder(orderId, { reason }),
    onSuccess: () => {
      toast.success('Order cancelled')
    },
    // Same reasoning as useCompleteOrder's onSettled above, including the `awaitRefresh` cap.
    // Also invalidates `["payments"]`: a successful cancel cancels the order's own open payment too.
    onSettled: (_data, _error, variables) =>
      awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ['admin-orders'] }),
          queryClient.invalidateQueries({ queryKey: ['admin-order', variables.orderId] }),
          queryClient.invalidateQueries({ queryKey: ['admin-order-statistics'] }),
          queryClient.invalidateQueries({ queryKey: ['payments'] }),
        ]),
      ),
    onError: (error: AxiosError | Error) => {
      // The gate refusals come back as 409 with a sentence saying which layer stopped it — the
      // order is past Pending, a payment is already confirmed, a machine has been built, or the
      // order changed concurrently. `error.message` on an AxiosError is "Request failed with
      // status code 409", which tells the operator nothing about which of those it was or what to
      // do instead.
      let errorMessage = 'Failed to cancel order'

      if (error instanceof AxiosError) {
        const apiError = error.response?.data
        if (apiError && typeof apiError === 'object' && 'error' in apiError) {
          errorMessage = (apiError as { error: { message: string } }).error.message
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
      } else {
        errorMessage = error.message
      }

      toast.error(errorMessage)
    },
  })
}
